import React, { useEffect } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { Formik } from 'formik';
import { ValidateForm } from 'Utils/Forms';
import SignUpForm, { isEmail, isPhoneNumber, signUpFormSchema } from 'Components/Forms/SignUpForm';
import SocialSignInButtons from 'Components/Forms/SocialSignInButtons';
import { AuthPendingOverlay, OrDivider, TermsAndPrivacyLine } from 'Components/Forms/AuthPageParts';
import { authColumnWidth } from 'Components/Forms/authColumn';
import { useEmailOrPhonePrefill } from 'Components/Forms/useEmailOrPhonePrefill';
import { MUSEFLOW_OFF_WHITE_1 } from 'Components/Colors';
import { SignUpData } from 'Types';
import VerificationCode from './VerificationCode';
import { useNavigate } from 'react-router-dom';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { setEmailPrefill } from 'Utils/EmailPrefill';

type SignUpProps = {
  goBackFn: () => void;
};

const SIGN_IN_PATH = '/signin';

const SignUp: React.FC<SignUpProps> = (props) => {
  const navigate = useNavigate();
  const {
    pendingEmailOrPhone,
    processPending,
    errors,
    existingAccountEmailOrPhone,
    oauthPending,
    resetErrors,
    signUpUsername,
  } = useAuthWrapperContext();

  // An email or phone number carried in by the link that opened this page.
  const emailOrPhonePrefill = useEmailOrPhonePrefill();

  // The carried value fills the form once. After a submit the form is shown
  // again empty, not with the address the page was opened with.
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const accountExistsNoticeRef = React.useRef<HTMLParagraphElement>(null);
  const signInInsteadLinkRef = React.useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (pendingEmailOrPhone && isEmail(pendingEmailOrPhone)) {
      navigate(SIGN_IN_PATH);
    }
  }, [pendingEmailOrPhone, navigate]);

  // The form comes back empty after a refused sign-up, and the button that had
  // focus is gone with it: put the reason on screen and focus on its link.
  useEffect(() => {
    if (existingAccountEmailOrPhone) {
      signInInsteadLinkRef.current?.focus({ preventScroll: true });
      accountExistsNoticeRef.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [existingAccountEmailOrPhone]);

  // An attempt belongs to one visit: arriving must not show an error left by
  // another page, and leaving must not keep the notice or a form error for the
  // next visit.
  useEffect(() => {
    resetErrors();
    return resetErrors;
  }, [resetErrors]);

  const { goBackFn: _goBackFn } = props;

  // The address goes across in memory, never in the page address.
  const goToSignInWithAccountPrefilled = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setEmailPrefill(existingAccountEmailOrPhone);
    resetErrors();
    navigate(SIGN_IN_PATH);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '0px',
        marginBottom: '20px',
        // Holds the card open while the form is hidden during a request.
        minHeight: '450px',
      }}
    >
      {oauthPending && <AuthPendingOverlay />}
      {/* {
        pendingEmailOrPhone && isEmail(pendingEmailOrPhone) &&
        <VerificationPending
        resendFn={() => {dispatch(authActions.resendSignUp(pendingEmailOrPhone || ""))}}
        goBackFn={() => {
          dispatch(authActions.setPendingEmailOrPhone(undefined))
          goBackFn()
        }}
        />
      } */}
      {pendingEmailOrPhone && isPhoneNumber(pendingEmailOrPhone) && (
        <VerificationCode
        // resendFn={() => {dispatch(authActions.resendSignUp(pendingEmailOrPhone || ""))}}
        // goBackFn={() => {
        //   dispatch(authActions.setPendingEmailOrPhone(undefined))
        //   goBackFn()
        // }}
        />
      )}
      {!pendingEmailOrPhone && (
        <>
          <SocialSignInButtons />
          <OrDivider />
          {existingAccountEmailOrPhone && (
            <Typography
              ref={accountExistsNoticeRef}
              role="alert"
              sx={{
                width: authColumnWidth,
                maxWidth: authColumnWidth,
                boxSizing: 'border-box',
                marginBottom: '16px',
                padding: '10px',
                borderRadius: '4px',
                backgroundColor: 'rgba(57, 94, 227, 0.40)',
                textAlign: 'center',
              }}
              color="#00C2FF"
            >
              You already have an account.{' '}
              <Link
                ref={signInInsteadLinkRef}
                href={SIGN_IN_PATH}
                onClick={goToSignInWithAccountPrefilled}
                sx={{ color: MUSEFLOW_OFF_WHITE_1, textDecorationColor: MUSEFLOW_OFF_WHITE_1 }}
              >
                Sign in instead
              </Link>
            </Typography>
          )}
          {!processPending && (
            <Formik
              initialValues={{
                name: '',
                emailOrPhone: hasSubmitted ? '' : emailOrPhonePrefill,
                password: '',
                repeatPassword: '',
                unknown: '',
                countryAcronym: 'US',
              }}
              validate={async (values: SignUpData) => {
                return ValidateForm(signUpFormSchema, values);
              }}
              onSubmit={async (values) => {
                setHasSubmitted(true);
                signUpUsername(values);
              }}
              validateOnChange={false}
            >
              {(formikProps) => (
                <form
                  autoComplete="on"
                  onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                    e.preventDefault();
                    resetErrors();
                    formikProps.handleSubmit(e);
                  }}
                >
                  <Box
                    sx={{
                      width: authColumnWidth,
                      maxWidth: authColumnWidth,
                      display: 'flex',
                      flexDirection: 'column',
                      // Fields span the full column, in line with the buttons above.
                      '& .signup-form-container': { paddingLeft: 0, paddingRight: 0 },
                    }}
                  >
                    <SignUpForm {...formikProps} errors={{ ...errors, ...formikProps.errors }} />
                  </Box>
                </form>
              )}
            </Formik>
          )}
          <TermsAndPrivacyLine />
        </>
      )}
      <Box
        sx={{
          color: MUSEFLOW_OFF_WHITE_1,
          marginTop: '20px',
          fontFamily: 'Lato',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: 'normal',
          letterSpacing: '0.16px',
        }}
      >
        Already have an account? &nbsp;{' '}
        <Link href={SIGN_IN_PATH} sx={{ color: '#6F87F1' }}>
          {' '}
          Sign in
        </Link>
      </Box>
    </Box>
  );
};

export default SignUp;
